import { motion } from 'motion/react';
import {
  ArrowRight,
  Link2,
  Lock,
  MessageSquare,
  Mic,
  MonitorPlay,
  Play,
  ShieldCheck,
  Users,
  Video,
} from 'lucide-react';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { JoinRoomForm } from '@/features/invitations/JoinRoomForm';
import { CreateRoomDialog } from '@/features/rooms/CreateRoomDialog';
import { useI18n } from '@/locales';

const featureIcons = [MonitorPlay, Mic, MessageSquare, Link2];
const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
};

export function LandingPage() {
  const { m } = useI18n();
  const focusJoin = () => document.getElementById('quick-join')?.focus();
  const createButton = (
    <Button variant="primary" size="lg">
      {m.common.createRoom} <ArrowRight />
    </Button>
  );

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-ink-950">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-56 left-1/2 h-[36rem] w-[60rem] max-w-[200vw] -translate-x-1/2 rounded-full bg-volt-600/14 blur-[140px]"
      />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center gap-4 px-4 py-5 sm:px-6">
        <Logo />
        <nav className="ml-6 hidden gap-6 text-sm font-medium text-fg-muted md:flex">
          <a href="#how" className="hover:text-fg">
            {m.landing.navHow}
          </a>
          <a href="#privacy" className="hover:text-fg">
            {m.landing.navPrivacy}
          </a>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <LanguageSwitch />
          <CreateRoomDialog
            trigger={
              <Button variant="secondary" size="sm" className="hidden sm:inline-flex">
                {m.common.createRoom}
              </Button>
            }
          />
        </div>
      </header>

      <main className="relative z-10">
        <section className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-20 pt-10 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-volt-500/10 px-3 py-1 text-xs font-bold tracking-wide text-volt-300 ring-1 ring-volt-500/25">
              <span className="size-1.5 rounded-full bg-volt-400" /> {m.landing.eyebrow}
            </p>
            <h1 className="whitespace-pre-line text-display font-extrabold tracking-[-0.035em]">
              {m.landing.title}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-fg-muted">
              {m.landing.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CreateRoomDialog trigger={createButton} />
              <Button variant="outline" size="lg" onClick={focusJoin}>
                {m.common.joinRoom}
              </Button>
            </div>
            <JoinRoomForm className="mt-6 max-w-lg" />
            <p className="mt-6 text-sm font-semibold tracking-wide text-fg-subtle">
              {m.common.tagline}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <DemoRoom />
          </motion.div>
        </section>

        <section id="how" className="mx-auto max-w-6xl scroll-mt-8 px-4 py-16 sm:px-6">
          <motion.h2
            {...fadeUp}
            className="max-w-2xl text-3xl font-extrabold tracking-tight sm:text-4xl"
          >
            {m.landing.featuresTitle}
          </motion.h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {m.landing.features.map((feature, i) => {
              const Icon = featureIcons[i] ?? MonitorPlay;
              return (
                <motion.article
                  key={feature.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                  className="rounded-2xl bg-ink-900 p-6 ring-1 ring-line"
                >
                  <span className="grid size-11 place-items-center rounded-xl bg-volt-500/12 text-volt-300 ring-1 ring-volt-500/25">
                    <Icon className="size-5" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fg-muted">{feature.text}</p>
                </motion.article>
              );
            })}
          </div>

          <motion.div
            {...fadeUp}
            className="mt-16 rounded-3xl bg-ink-900 p-6 ring-1 ring-line sm:p-10"
          >
            <h3 className="text-2xl font-extrabold tracking-tight">{m.landing.stepsTitle}</h3>
            <ol className="mt-8 grid gap-6 sm:grid-cols-3">
              {m.landing.steps.map((step, i) => (
                <li key={step} className="flex items-center gap-4">
                  <span className="tabular grid size-11 shrink-0 place-items-center rounded-full bg-ink-700 text-lg font-extrabold text-volt-300 ring-1 ring-line-strong">
                    {i + 1}
                  </span>
                  <span className="text-[1.05rem] font-semibold">{step}</span>
                </li>
              ))}
            </ol>
          </motion.div>
        </section>

        <section id="privacy" className="mx-auto max-w-6xl scroll-mt-8 px-4 py-16 sm:px-6">
          <motion.div
            {...fadeUp}
            className="grid gap-10 rounded-3xl bg-gradient-to-br from-ink-800 to-ink-900 p-6 ring-1 ring-line sm:p-10 lg:grid-cols-[1fr_1.4fr]"
          >
            <div>
              <span className="grid size-12 place-items-center rounded-2xl bg-success/10 text-success ring-1 ring-success/25">
                <ShieldCheck className="size-6" strokeWidth={1.75} />
              </span>
              <h2 className="mt-5 text-3xl font-extrabold tracking-tight">
                {m.landing.privacyTitle}
              </h2>
            </div>
            <ul className="space-y-4">
              {m.landing.privacy.map((item) => (
                <li key={item} className="flex gap-3 text-[0.98rem] leading-relaxed text-fg-muted">
                  <Lock className="mt-1 size-4 shrink-0 text-volt-300" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-sm text-fg-subtle sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <Logo className="[&_svg]:size-6 [&>span]:text-sm" />
            <span>{m.landing.footer}</span>
          </div>
          <span>{m.landing.footerNote}</span>
        </div>
      </footer>
    </div>
  );
}

/** Purely illustrative, non-interactive picture of a room (aria-hidden, no controls). */
function DemoRoom() {
  const { m } = useI18n();
  return (
    <figure aria-label={m.landing.demoLabel} className="relative">
      <div
        aria-hidden
        className="rounded-[1.75rem] bg-ink-900 p-2.5 shadow-panel ring-1 ring-line-strong"
      >
        <div className="relative aspect-video overflow-hidden rounded-2xl bg-black">
          <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_30%_20%,rgb(127_102_255/0.45),transparent_55%),radial-gradient(90%_70%_at_80%_90%,rgb(255_93_108/0.25),transparent_60%),linear-gradient(180deg,#14121f,#050507)]" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-bold text-white/85 backdrop-blur">
            <span className="size-1.5 rounded-full bg-success" /> {m.landing.demoSynced}
          </div>
          <div className="absolute right-3 top-3 grid w-[34%] grid-cols-2 gap-1.5 rounded-xl bg-black/35 p-1.5 backdrop-blur">
            {[210, 330].map((hue, i) => (
              <div
                key={hue}
                className={`relative aspect-[4/3] rounded-lg ring-2 ${i === 0 ? 'ring-success' : 'ring-white/10'}`}
                style={{
                  background: `linear-gradient(135deg, hsl(${hue} 45% 35%), hsl(${hue + 30} 50% 18%))`,
                }}
              >
                {i === 0 ? (
                  <Mic className="absolute bottom-1 right-1 size-2.5 text-white/80" />
                ) : (
                  <Video className="absolute bottom-1 right-1 size-2.5 text-white/80" />
                )}
              </div>
            ))}
          </div>
          <div className="absolute inset-x-4 bottom-3 flex items-center gap-3 text-white">
            <Play className="size-4 fill-current" />
            <div className="h-1 flex-1 rounded-full bg-white/20">
              <div className="h-full w-[38%] rounded-full bg-volt-400" />
            </div>
            <span className="tabular text-[11px] font-semibold text-white/80">48:12</span>
          </div>
        </div>
        <div className="mt-2.5 grid grid-cols-[1fr_auto] gap-2.5 px-1 pb-1">
          <div className="space-y-1.5">
            <DemoMessage hue={210} text={m.landing.demoChat1} />
            <DemoMessage hue={330} text={m.landing.demoChat2} />
          </div>
          <div className="flex flex-col items-end justify-between text-[11px] text-fg-subtle">
            <span className="inline-flex items-center gap-1">
              <Users className="size-3" /> 2
            </span>
            <span>{m.landing.demoNowPlaying}</span>
          </div>
        </div>
      </div>
    </figure>
  );
}

function DemoMessage({ hue, text }: { hue: number; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="size-5 shrink-0 rounded-full"
        style={{
          background: `linear-gradient(135deg, hsl(${hue} 55% 42%), hsl(${hue + 40} 60% 30%))`,
        }}
      />
      <span className="truncate rounded-lg bg-ink-700 px-2.5 py-1 text-xs text-fg/85">{text}</span>
    </div>
  );
}
